import { ProfileSkillsListService } from '@app/profile/services/profile-skills-list.service';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { isKey, Key } from '@app/shared/key';
import {
  isRatingTypeAvailable,
  tagHasIncompleteRatingsForType,
} from '@app/shared/utils/tag-helpers';
import { EvaluationService } from '@app/tags/services/evaluation.service';
import { TagRatingTrackerService } from '@app/tags/services/tag-rating-tracker.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@app/shared/services/auth.service';
import { TagsService } from '@app/tags/services/tags.service';

@Component({
  selector: 'dgx-profile-skills-list-skill-review',
  templateUrl: './profile-skills-list-skill-review.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSkillsListSkillReviewComponent implements OnInit {
  @Input() public tag: TagsApi.TagDetails;
  @Input() public isOwner: boolean;
  @Input() public canEvaluateSkills: boolean;
  @Input() public showLock: boolean;

  public i18n: { [key: string]: string };

  constructor(
    private tagsService: TagsService,
    private translateService: TranslateService,
    private tagRatingService: TagRatingService,
    private tagRatingTrackerService: TagRatingTrackerService,
    private evaluationService: EvaluationService,
    private authService: AuthService,
    private profileSkillsListService: ProfileSkillsListService
  ) {}

  public get rating(): TagsApi.UserTagRating {
    return this.tag.ratings?.find(
      (rating) => rating.type === InternalTagRatingTypes.evaluation
    );
  }

  public get level(): string | undefined {
    return this.rating?.level;
  }

  public get inProgress(): boolean {
    return (
      (this.rating && !this.rating.level) ||
      tagHasIncompleteRatingsForType(
        this.tag,
        InternalTagRatingTypes.evaluation
      )
    );
  }

  public get showDonut(): boolean {
    return (
      (this.isOwner && this.isAvailable && this.canEvaluateSkills) ||
      !!this.level
    );
  }

  public get showDefaultPlus(): boolean {
    return !this.level && !this.inProgress;
  }

  public get tabIndex(): 0 | undefined {
    return this.isOwner ? 0 : undefined;
  }

  public get role(): 'button' | undefined {
    return this.isOwner ? 'button' : undefined;
  }

  private get levelText(): string {
    return this.profileSkillsListService.getLevelText(this.level);
  }

  private get baseText(): string {
    let text = this.i18n.dgTagRating_Evaluation;

    if (this.isOwner && !this.level && !this.inProgress) {
      text = this.i18n.dgTagRating_AddEvaluation;
    }

    return text;
  }

  private get privacyText(): string {
    return this.profileSkillsListService.getPrivacyText(
      this.level,
      this.isOwner,
      this.showLock,
      this.authService.authUser?.participateInTeamProgram ?? false
    );
  }

  public get ariaLabel(): string {
    return this.profileSkillsListService.getAriaLabelText(
      this.tagRatingService.getAriaLabelForRatingDonut(
        this.level,
        this.inProgress,
        this.tag
      ),
      this.privacyText,
      ''
    );
  }

  public get tooltip(): string {
    return this.profileSkillsListService.getTooltipText(
      this.baseText,
      this.privacyText
    );
  }

  public get isAvailable(): boolean {
    return isRatingTypeAvailable(this.tag, InternalTagRatingTypes.evaluation);
  }

  public ngOnInit(): void {
    this.i18n = this.translateService.instant([
      'dgTagRating_InProgress',
      'dgTagRating_Evaluation',
      'dgTagRating_AddEvaluation',
      'dgTagRating_SkillReviewInProgress',
      'Core_Private',
      'Core_LimitedVisibility',
    ]);
  }

  public clickHandler(event: MouseEvent, tagName: string, tagId: number): void {
    this.eventHandler(event, tagName, tagId);
  }

  public keydownHandler(
    event: KeyboardEvent,
    tagName: string,
    tagId: number
  ): void {
    if (isKey(event, Key.Enter, Key.Space)) {
      // prevent spacebar from scrolling screen
      event.preventDefault();
      this.eventHandler(event, tagName, tagId);
    }
  }

  private eventHandler(event: Event, tagName: string, tagId: number): void {
    if (this.isOwner) {
      // prevent event from triggering skill modal
      event.stopPropagation();
      if (this.inProgress) {
        this.tagRatingTrackerService.trackRatingUpdateStepCompleted(
          event.target as HTMLElement,
          this.tag,
          InternalTagRatingTypes.evaluation,
          'profile-skills-eval-cta',
          'Continue'
        );
        this.evaluationService.continueEvaluation(tagName, tagId);
      } else if (this.level) {
        this.evaluationService.reviewEvaluationResults(tagName, tagId);
      } else {
        this.evaluationService.startEvaluation(
          this.tag,
          event.target as HTMLElement
        );
      }
    }
  }

  public getLevelDescription(level: string): string {
    return this.tagsService.orgLevels.find((l) => l.value.toString() === level)
      ?.description;
  }
}
